import { useTranslation } from 'react-i18next';
import { WIDTH_CLASSES } from '@xxtract/ui';
import { formatGetal, isGetal } from '../format';
import { GeenMeting } from './MeasurementValue';
import type { Categoriestaaf } from '../work-quality.api';

export interface CategorieStavenProps {
  categorieen: Categoriestaaf[];
}

/**
 * De Onderdeelbalken van het tabblad AI (EXPERIENCE.md S6): per onderdeel uit het rapport één balk,
 * in de volgorde van de dienst, zonder vergelijking met vorige week.
 *
 * **Met de hand getekend, en dat is een eis.** Er komt geen grafiekbibliotheek in dit portaal — de
 * reden staat in `BehandeltijdGrafiek.tsx:17-24`: zulke bibliotheken vullen een ontbrekend punt
 * standaard met nul, en dan wordt "niet gemeten" stilzwijgend een gemeten nul. De balk gebruikt
 * `WIDTH_CLASSES` uit `@xxtract/ui`.
 *
 * `role="img"` staat alleen om de balk, met het getal in woorden; naam en percentage ernaast blijven
 * gewone tekst. De vulling is `--chart-neutral`: een balk die niet vergelijkt, oordeelt niet.
 *
 * De onderdeelnamen komen ruw uit de meetdienst en worden niet vertaald; een tweede lijst in dit
 * portaal zou stil verouderen zodra er in de dienst een onderdeel bijkomt.
 */
export function CategorieStaven({ categorieen }: CategorieStavenProps) {
  const { t } = useTranslation();
  // Een lege lijst betekent dat het rapport geen onderdelen droeg. Een kop zonder rijen zou beloven
  // dat er iets te zien is.
  if (categorieen.length === 0) return null;

  return (
    <section className="mb-4" data-testid="werkkwaliteit-onderdeelbalken">
      <h3 className="mb-2 text-sm font-semibold text-[var(--foreground-heading)]">{t('qa.ai.onderdelenKop')}</h3>
      <dl className="space-y-2" data-testid="werkkwaliteit-categoriestaven">
        {/* De index in de sleutel: twee onderdelen met dezelfde naam zijn niet te verwachten, maar
            als het toch gebeurt zou React de tweede rij met de eerste verwarren. */}
        {categorieen.map((rij, index) => (
          <Categorierij key={`${rij.categorie}-${index}`} rij={rij} />
        ))}
      </dl>
    </section>
  );
}

/**
 * De breedte van deze staaf, of `null` als er geen te tekenen valt.
 *
 * Een opzoeking en geen berekening. De dienst stuurt een veelvoud van vijf mee; wat de tabel niet
 * kent — geen veelvoud van vijf, buiten 0 tot 100, of helemaal afwezig omdat er niets gemeten is —
 * levert géén balk, en niet een zelf bedachte: het scherm rekent niet over getallen die het afdrukt
 * (AD-19/NFR9).
 */
function breedteVan(rij: Categoriestaaf): string | null {
  // Ook geen balk zonder waarde. De dienst stuurt die twee samen of geen van beide, maar een balk
  // naast de badge "Niet gemeten" zou het ergste van twee werelden zijn: een gekleurde staaf die
  // een meting suggereert, met de mededeling ernaast dat er niets gemeten is.
  if (!isGetal(rij.waarde) || !isGetal(rij.staafBreedte)) return null;
  return WIDTH_CLASSES[rij.staafBreedte] ?? null;
}

function Categorierij({ rij }: { rij: Categoriestaaf }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const breedte = breedteVan(rij);

  return (
    <div data-testid={`werkkwaliteit-categoriestaaf-${rij.categorie}`}>
      {/* Een leesbreedte op de tekstregel, en alleen daar.
          Gezien op productie op 1920 px: `justify-between` over de volle kaartbreedte zette de naam
          links en het percentage ruim duizend pixels verderop (gemeten: 1354 px gat), en dan is
          niet meer te zien welk getal bij welke categorie hoort. Met een maximum staan ze op
          leesafstand én lijnen de negen getallen nog in één kolom uit. Op 375 px bindt dit maximum
          niet, en daar zag de visuele controle het gebrek dus niet. De baan eronder houdt wél de
          volle breedte: een korte staaf is minder precies af te lezen.

          **In pixels en niet in `rem`, anders dan de vijf `max-w-[46rem]` elders in dit portaal.**
          `--font-scale` zet `html { font-size: calc(16px * scale) }` (`globals.css:121`), dus een
          `rem`-maat groeit mee met de lettergrootte-instelling — terwijl álle tekst in dit blok op
          `text-[12px]` vastligt en dus niet meegroeit. Gemeten met `max-w-2xl`: een gat van 530 px
          op "normaal" en 696 px op "extra groot". Het gebrek werd zo juist een kwart erger bij de
          instelling die het het hardst nodig heeft. */}
      <div
        className="flex max-w-[640px] flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5"
        data-testid="werkkwaliteit-staaftekstrij"
      >
        <dt className="text-[12px] font-medium text-[var(--foreground)]">{rij.categorie}</dt>
        <dd className="flex items-baseline gap-2 text-[12px]">
          {/* `isGetal` en niet `isTelling`: een AAS kán onder nul uitkomen — `veldkansen` telt
              alleen velden die in het doelmoment staan, terwijl hallucinaties juist lege velden
              tellen. Dat is dan wél een meting, en hoort als getal te blijven staan. Alleen
              afwezigheid krijgt de badge. */}
          {isGetal(rij.waarde) ? (
            <span
              className="font-semibold text-[var(--foreground)]"
              data-testid="werkkwaliteit-staafwaarde"
            >
              {formatGetal(rij.waarde, locale)}%
            </span>
          ) : (
            // Dezelfde badge als de rest van deze module, en niet een eigen nabootsing: het
            // `help`-icoon en `data-werkkwaliteit-status` zijn er onderdeel van.
            <GeenMeting label={t('qa.alle.kerngetalNietGemeten')} />
          )}
        </dd>
      </div>
      {/* Zonder balk géén volle baan maar een gestreepte lijn.
          Gezien op 16 september 2026 in donkere modus op 375 px: een volle grijze baan onder
          "Niet gemeten" leest als een staaf die tot nul is gezakt — precies de verwarring die dit
          hele blok moet vermijden. `BehandeltijdGrafiek` tekent een leeg tijdvak om dezelfde reden
          gestreept en niet als een lege baan. */}
      {breedte === null || !isGetal(rij.waarde) ? (
        <div
          aria-hidden="true"
          className="mt-1 h-1.5 w-full border-t-2 border-dashed border-[var(--border)]"
          data-testid="werkkwaliteit-staafleeg"
        />
      ) : (
        <div
          aria-label={t('qa.ai.onderdeelBalk', { onderdeel: rij.categorie, waarde: formatGetal(rij.waarde, locale) })}
          className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-[var(--border)]"
          role="img"
        >
          <div
            aria-hidden="true"
            className={`h-full rounded-full bg-[var(--chart-neutral)] ${breedte}`}
            data-testid="werkkwaliteit-staafbalk"
          />
        </div>
      )}
    </div>
  );
}
